import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, QrCode, Check, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSettings = () => {
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/settings/payment`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCode(response.data.qr_code);
      setUpdatedAt(response.data.updated_at);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        const token = localStorage.getItem('admin_token');
        await axios.put(`${API}/admin/settings/payment`, {
          qr_code_base64: base64
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setQrCode(base64);
        setUpdatedAt(new Date().toISOString());
        setSuccess('Payment QR code updated successfully!');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload QR code');
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="payment-settings-page" className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Payment Settings
        </h1>
        <p className="text-slate-600 mt-1">
          Configure the UPI QR code that customers will use to pay for subscriptions
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 flex items-center gap-2">
          <Check size={20} />
          {success}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <QrCode className="text-violet-600" size={24} />
            Payment QR Code
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            This QR code will be displayed to customers when they select a subscription plan
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Current QR Preview */}
          <div className="flex flex-col items-center">
            {qrCode ? (
              <div className="relative">
                <img 
                  src={qrCode} 
                  alt="Payment QR Code" 
                  className="w-64 h-64 object-contain border-2 border-slate-200 rounded-xl p-2"
                  data-testid="qr-preview"
                />
                {updatedAt && (
                  <p className="text-xs text-slate-400 text-center mt-2">
                    Last updated: {new Date(updatedAt).toLocaleString('en-IN')}
                  </p>
                )}
              </div>
            ) : (
              <div className="w-64 h-64 bg-slate-100 rounded-xl flex flex-col items-center justify-center text-slate-400">
                <QrCode size={48} />
                <p className="mt-2 text-sm">No QR code uploaded</p>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div className="flex flex-col items-center">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="qr-upload-input"
              />
              <div className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                uploading 
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:shadow-lg'
              }`}>
                <Upload size={20} />
                {uploading ? 'Uploading...' : qrCode ? 'Replace QR Code' : 'Upload QR Code'}
              </div>
            </label>
            <p className="text-xs text-slate-400 mt-2">
              Supported: PNG, JPG, JPEG (Max 5MB)
            </p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <h3 className="font-semibold text-amber-800 mb-2">Instructions</h3>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• Upload your UPI QR code (GPay, PhonePe, Paytm, etc.)</li>
          <li>• Customers will scan this QR to pay for their subscription</li>
          <li>• After payment, customers will submit their UTR/Transaction ID for verification</li>
          <li>• You can approve or reject payment requests from the Payment Approvals page</li>
        </ul>
      </div>
    </div>
  );
};

export default PaymentSettings;
